import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";

const CARD_BACK_SRC = "/cards/back.png";

const getCardImagePath = (card) => {
  if (!card) return CARD_BACK_SRC;
  if (card.isJoker) {
    return card.jokerColor === "red" ? "/cards/joker_red.png" : "/cards/joker_black.png";
  }
  return `/cards/${card.suit}_${card.fileValue}.png`;
};

/**
 * 3D card flip: back face → front face when `revealed` becomes true.
 * Skips animation on first paint so mid-game joins don't flash.
 */
const FlippableCard = ({
  card = null,
  revealed = false,
  maxHeight = { xs: 160, sm: 250, md: 300 },
  minHeight = { xs: 160, sm: 250, md: 300 },
  durationMs = 600,
  alt = "Card",
}) => {
  const [transitionsReady, setTransitionsReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setTransitionsReady(true));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const frontSrc = card ? getCardImagePath(card) : CARD_BACK_SRC;
  const frontAlt = card?.displayName || card?.name || alt;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        perspective: "1200px",
        minHeight: minHeight || 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: minHeight || 0,
          transformStyle: "preserve-3d",
          transition: transitionsReady
            ? `transform ${durationMs}ms cubic-bezier(0.4, 0.0, 0.2, 1)`
            : "none",
          transform: revealed ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Back */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(0deg)",
          }}
        >
          <Box
            component="img"
            src={CARD_BACK_SRC}
            alt="Card back"
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: maxHeight || "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>

        {/* Front */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <Box
            component="img"
            src={frontSrc}
            alt={frontAlt}
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: maxHeight || "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export { getCardImagePath };
export default FlippableCard;
